import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { AlertTriangle, Check, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useResetPassword } from '../../hooks/useUsers';

interface SetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName?: string;
}

const criteria = [
  { key: 'length', label: 'At least 12 characters', test: (p: string) => p.length >= 12 },
  { key: 'upper', label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { key: 'number', label: 'One number', test: (p: string) => /\d/.test(p) },
  { key: 'special', label: 'One special character', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

/**
 * Development-only dialog that sets a user's password directly without an
 * email round-trip. The backend rejects the `direct` mode outside of dev, so
 * this is safe to render conditionally on `import.meta.env.DEV` in callers.
 */
export function SetPasswordDialog({
  open,
  onOpenChange,
  userId,
  userName,
}: SetPasswordDialogProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const resetPassword = useResetPassword();

  useEffect(() => {
    if (open) {
      setNewPassword('');
      setConfirm('');
    }
  }, [open]);

  const results = useMemo(
    () => criteria.map((c) => ({ ...c, passed: c.test(newPassword) })),
    [newPassword],
  );

  const allPassed = results.every((r) => r.passed);
  const matches = newPassword.length > 0 && newPassword === confirm;
  const canSubmit = allPassed && matches;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await resetPassword.mutateAsync({ userId, data: { mode: 'direct', newPassword } });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Set Password Directly (Dev)</DialogTitle>
          <DialogDescription>
            {userName ? `Set a new password for ${userName}.` : 'Set a new password for this user.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-2 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-200">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              Development-only flow. In production environments use the standard{' '}
              <em>Reset Password</em> action which emails the user a temporary password.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              autoComplete="new-password"
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm Password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirm}
              autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value)}
            />
            {confirm.length > 0 && !matches && (
              <p className="text-xs text-red-600 dark:text-red-400">Passwords do not match.</p>
            )}
          </div>

          <ul className="space-y-1 text-xs">
            {results.map((c) => (
              <li
                key={c.key}
                className={`flex items-center gap-2 ${
                  c.passed ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'
                }`}
              >
                {c.passed ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                {c.label}
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={resetPassword.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || resetPassword.isPending}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {resetPassword.isPending ? 'Setting…' : 'Set Password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
